import { MapPin, Images } from "lucide-react";
import { useState, type FormEvent } from "react";
import { get, post, put, type Trip } from "../lib/api.ts";
import { landingSection, navigate, tripPath } from "../lib/router.tsx";
import { setPhoneLocation } from "../lib/session.ts";
import { Card, ICON, NestedCard, reason } from "../ui/index.ts";

/**
 * B. Trip invitation. Joining is one step; what to share is a separate, explicit step where nothing
 * is pre-ticked. It says plainly that every member's full route is visible to the whole trip.
 *
 * That step is a traveller's. An editor is invited to cut the footage, not to shoot it: nothing of
 * theirs is uploaded and no route of theirs is recorded, so asking them for a camera roll and a
 * location would be asking for something the trip will never read.
 */
export function JoinPage({ token }: { token: string | null }) {
  const [code, setCode] = useState(token ?? "");
  const [trip, setTrip] = useState<Trip | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [shareMedia, setShareMedia] = useState(false);
  const [shareRoute, setShareRoute] = useState(false);

  async function join(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const r = await post<{ tripId: string }>("/v1/invitations/redeem", { token: code.trim() });
      const joined = await get<Trip>(`/v1/trips/${r.tripId}`);
      // Only a contributor has anything to share, so only a contributor is asked. Loaded rather
      // than navigated for the same reason as below: memberships come from /v1/me.
      if (!joined.roles.includes("CONTRIBUTOR")) {
        window.location.assign(tripPath(joined.id, landingSection(joined.roles)));
        return;
      }
      setTrip(joined);
    } catch (err) {
      setError(reason(err as Error));
    } finally {
      setBusy(false);
    }
  }

  async function finish() {
    if (!trip) return;
    setError(null);
    setBusy(true);
    try {
      for (const [scope, on] of [
        ["MEDIA", shareMedia],
        ["ROUTE", shareRoute],
      ] as const) {
        if (!on) continue;
        const cur = await get<{ version: number }>(`/v1/trips/${trip.id}/consents/${scope}`);
        await put(`/v1/trips/${trip.id}/consents/${scope}`, {
          decision: "GRANTED",
          baseVersion: cur.version,
        });
      }
      // On the phone, sharing the route starts recording it now (with its own permission prompt).
      if (shareRoute) await setPhoneLocation(trip.id, true).catch(() => undefined);
      // Memberships come from /v1/me, so load the trip fresh.
      window.location.assign(tripPath(trip.id));
    } catch (err) {
      setError(reason(err as Error));
      setBusy(false);
    }
  }

  if (!trip) {
    return (
      <section className="stack" style={{ maxWidth: "32rem" }}>
        <h1>Join a trip</h1>
        <form className="card" onSubmit={join}>
          <p className="muted">
            {token
              ? "You’ve been invited to a trip. Joining doesn’t share anything yet; you choose that next."
              : "Paste the invitation code you received."}
          </p>
          {!token && (
            <label className="field">
              <span>Invitation code</span>
              <input value={code} onChange={(e) => setCode(e.target.value)} required />
            </label>
          )}
          {error && (
            <p role="alert" className="error">
              {error}
            </p>
          )}
          <div>
            <button className="button primary" type="submit" disabled={busy || !code.trim()}>
              Join trip
            </button>
          </div>
        </form>
      </section>
    );
  }

  return (
    <section className="stack" style={{ maxWidth: "36rem" }}>
      <div>
        <h1>{trip.name}</h1>
        {trip.destination && <p className="muted">{trip.destination}</p>}
      </div>
      <Card title="Choose what to share">
        <p className="muted">Nothing is shared until you choose. You can change this later.</p>
        <label className="check">
          <input
            type="checkbox"
            checked={shareMedia}
            onChange={(e) => setShareMedia(e.target.checked)}
          />
          <span className="radio-text">
            <span className="row-title">
              <Images {...ICON} aria-hidden="true" /> Share my photos and videos
            </span>
            <span className="row-detail">
              Only media taken during the trip is offered automatically. Anything else you add
              yourself.
            </span>
          </span>
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={shareRoute}
            onChange={(e) => setShareRoute(e.target.checked)}
          />
          <span className="radio-text">
            <span className="row-title">
              <MapPin {...ICON} aria-hidden="true" /> Share my route
            </span>
            <span className="row-detail">
              Your phone records where you are during the trip so footage can be placed on the map.
            </span>
          </span>
        </label>
        <NestedCard>
          <strong>Everyone on this trip can see your full route</strong> for the whole trip, not
          only where your photos were taken. Location sharing shows as “on” in the app while it is
          active, and you can stop it at any time.
        </NestedCard>
        {error && (
          <p role="alert" className="error">
            {error}
          </p>
        )}
        <div className="row">
          <button className="button" type="button" onClick={() => navigate(tripPath(trip.id))}>
            Not now
          </button>
          <span className="spacer" />
          <button className="button primary" type="button" onClick={finish} disabled={busy}>
            Continue
          </button>
        </div>
      </Card>
    </section>
  );
}
